import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { getCategoryBySlug } from '../config/categories';
import { useAuth } from '../hooks/useAuth';
import { useListings } from '../hooks/useListings';
import { useDirectorySearch } from '../hooks/useDirectorySearch';
import { useNearMe } from '../hooks/useNearMe';
import { listingDistance } from '../utils/directorySearch';
import { Listing } from '../types';
import DirectoryFilterBar from '../components/directory/DirectoryFilterBar';
import DirectoryMapView from '../components/directory/DirectoryMapView';
import ListingCard from '../components/directory/ListingCard';
import CategoryIcon from '../components/directory/CategoryIcon';
import LatestMessagesStrip from '../components/directory/LatestMessagesStrip';
import { NearMeButton, NearMeNotice } from '../components/directory/NearMe';

const FLOAT_SHADOW = 'shadow-[0_2px_6px_rgba(0,0,0,.04),0_4px_8px_rgba(0,0,0,.10)]';

/** /directory (All Services) and /directory/:slug (one category). */
export default function DirectoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug?: string }>();
  const config = slug ? getCategoryBySlug(slug) : undefined;
  usePageTitle(
    slug ? (config ? t(`seo.categories.${config.slug}`) : t('directory.detail.notFound')) : t('seo.directory'),
    slug
      ? config
        ? { description: t(`seo.desc.categories.${config.slug}`) }
        : { noindex: true }
      : { description: t('seo.desc.directory') }
  );
  const { user, loading: authLoading } = useAuth();
  const isSuperAdmin = user?.role === 'superAdmin';

  // The super admin also sees drafts, pending and hidden listings (with a tag).
  const { listings, loading, error } = useListings({
    category: config?.key,
    includeUnpublished: isSuperAdmin,
    skip: authLoading || (!!slug && !config),
  });
  const nearMe = useNearMe();
  const origin = nearMe.location;
  const { results, filters, updateFilters, clearFilters, hoursById, nextShiurById } = useDirectorySearch(listings, origin);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'map' ? 'map' : 'list';

  const distances = useMemo(
    () => (origin ? new Map(results.map((l) => [l.id, listingDistance(l, origin)])) : null),
    [results, origin]
  );
  const distanceOf = useCallback((l: Listing) => distances?.get(l.id), [distances]);

  if (slug && !config) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center space-y-4">
        <p className="text-ink">{t('directory.detail.notFound')}</p>
        <Link to="/directory" className="text-sm text-ink underline">
          {t('directory.detail.backToAll')}
        </Link>
      </div>
    );
  }

  const title = config ? t(`directory.categoryPlural.${config.key}`) : t('directory.allServices');

  const setView = (next: 'list' | 'map') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'map') params.set('view', 'map');
    else {
      params.delete('view');
      params.delete('item');
    }
    setSearchParams(params, { replace: true });
  };

  const viewToggle = (
    <div className="flex rounded-full border border-ink/15 p-0.5 shrink-0 bg-paper" role="group">
      {(['list', 'map'] as const).map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={view === v}
          onClick={() => setView(v)}
          className={`px-3 min-h-9 rounded-full text-sm font-medium transition-colors ${
            view === v ? 'bg-ink text-paper' : 'text-ink'
          }`}
        >
          {t(`directory.map.${v}View`)}
        </button>
      ))}
    </div>
  );

  const nearMeButton = <NearMeButton nearMe={nearMe} />;
  const notices = <NearMeNotice nearMe={nearMe} />;

  const filterBar = (
    <DirectoryFilterBar category={config?.key} filters={filters} onChange={updateFilters} onClear={clearFilters} />
  );

  const isLoading = loading || authLoading;

  const heading = (
    <div className="space-y-1 min-w-0">
      {config && (
        <Link to="/directory" className="text-sm text-ash hover:text-ink">
          {t('directory.title')}
        </Link>
      )}
      <h1 className="flex items-center gap-2 text-lg font-semibold text-ink">
        {config && <CategoryIcon category={config.key} className="w-5 h-5" />}
        {title}
      </h1>
    </div>
  );

  if (view === 'map' && !isLoading && !error) {
    return (
      <>
        {/* Desktop/tablet: page heading above the map */}
        <div className="hidden sm:block max-w-6xl mx-auto px-4 pt-6 pb-4">
          <div className="flex items-end justify-between gap-3">
            {heading}
            <div className="flex items-center gap-2">
              {nearMeButton}
              {viewToggle}
            </div>
          </div>
        </div>

        {/* Phone: floating buttons over the full-screen map */}
        <div className="sm:hidden fixed z-[35] top-[calc(var(--nav-bottom)+0.75rem)] inset-x-4 flex justify-between gap-2 pointer-events-none">
          <div className={`pointer-events-auto rounded-full ${FLOAT_SHADOW}`}>{nearMeButton}</div>
          <div className={`pointer-events-auto rounded-full ${FLOAT_SHADOW}`}>{viewToggle}</div>
        </div>

        <div className="sm:max-w-6xl sm:mx-auto sm:px-4 sm:pb-6">
          <DirectoryMapView
            results={results}
            hoursById={hoursById}
            distanceOf={distanceOf}
            userLocation={origin}
            filterBar={filterBar}
            notices={notices}
            title={title}
            focusId={searchParams.get('item')}
          />
        </div>
      </>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-end justify-between gap-3">
        {heading}
        {viewToggle}
      </div>

      {!isLoading && !error && <LatestMessagesStrip listings={listings} />}

      {filterBar}

      <div className="flex flex-wrap items-center gap-2">{nearMeButton}</div>
      {notices}

      {isLoading ? (
        <div className="space-y-3" aria-busy="true" aria-label={t('common.loading')}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[76px] rounded-[16px] bg-fog animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-danger-600">{t('common.error')}</p>
      ) : results.length === 0 ? (
        <p className="py-10 text-center text-ash">{t('directory.filter.noResults')}</p>
      ) : (
        <>
          <p className="text-xs text-ash">{t('directory.filter.results', { count: results.length })}</p>
          <ul className="space-y-3">
            {results.map((listing) => (
              <li key={listing.id}>
                <ListingCard listing={listing} hours={hoursById[listing.id]} distance={distanceOf(listing)} nextShiur={nextShiurById[listing.id]} />
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="rounded-[16px] border border-dashed border-ink/20 p-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ash">{t('directory.admin.add.prompt')}</p>
        <Link
          to={`/directory/add${config ? `?category=${config.key}` : ''}`}
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-ink/20 text-sm text-ink hover:border-ink/50"
        >
          <Plus className="w-4 h-4" aria-hidden />
          {t('directory.admin.addListing')}
        </Link>
      </div>
    </div>
  );
}
